import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Save, ShieldCheck, Search, Users as UsersIcon, CheckSquare, Square, RefreshCw } from "lucide-react";
import { toast } from "sonner";

/**
 * User Permissions Matrix — admin-only.
 * Rows = users, Columns = modules. Ticking a cell grants the user access.
 * Empty permissions list on a user = full access (backward-compat).
 */
export default function UserPermissionsPage() {
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dirty, setDirty] = useState({});   // {userId: Set<moduleKey>}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/permissions/matrix");
      setModules(data.modules);
      setUsers(data.users);
      setDirty({});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load matrix");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const g = {};
    modules.forEach((m) => { (g[m.group] ??= []).push(m); });
    return g;
  }, [modules]);

  const filteredUsers = useMemo(() => {
    const q = search.toLowerCase();
    return users.filter((u) => !q || u.email.toLowerCase().includes(q) || (u.name || "").toLowerCase().includes(q) || (u.role || "").toLowerCase().includes(q));
  }, [users, search]);

  const isChecked = (u, mKey) => {
    if (dirty[u.id] !== undefined) return dirty[u.id].has(mKey);
    return (u.module_permissions || []).includes(mKey);
  };

  const toggle = (u, mKey) => {
    setDirty((d) => {
      const current = d[u.id] !== undefined ? new Set(d[u.id]) : new Set(u.module_permissions || []);
      if (current.has(mKey)) current.delete(mKey);
      else current.add(mKey);
      return { ...d, [u.id]: current };
    });
  };

  const setAllForUser = (u, val) => {
    setDirty((d) => ({ ...d, [u.id]: val ? new Set(modules.map((m) => m.key)) : new Set() }));
  };

  const setAllForModule = (mKey, val) => {
    setDirty((d) => {
      const next = { ...d };
      filteredUsers.forEach((u) => {
        const cur = next[u.id] !== undefined ? new Set(next[u.id]) : new Set(u.module_permissions || []);
        if (val) cur.add(mKey); else cur.delete(mKey);
        next[u.id] = cur;
      });
      return next;
    });
  };

  const dirtyUserIds = Object.keys(dirty);
  const hasUnsaved = dirtyUserIds.length > 0;

  const saveAll = async () => {
    if (!hasUnsaved) return toast.info("Nothing to save.");
    setBusy(true);
    let failed = 0;
    try {
      await Promise.all(
        dirtyUserIds.map(async (uid) => {
          try {
            await api.patch(`/users/${uid}/permissions`, { module_permissions: [...dirty[uid]] });
          } catch {
            failed += 1;
          }
        })
      );
      if (failed) toast.error(`${failed} user(s) failed to update`);
      else toast.success(`Saved ${dirtyUserIds.length} user permission set(s)`);
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="user-permissions-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Administration</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            User Permissions
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Tick modules per user to grant sidebar access. Empty row = user follows their role default (full access for admin).
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <Input
              placeholder="Search users…"
              className="pl-8 h-9 w-56"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-users"
            />
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading || busy} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={saveAll}
            disabled={!hasUnsaved || busy}
            data-testid="save-permissions-btn"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {busy ? "Saving…" : hasUnsaved ? `Save (${dirtyUserIds.length})` : "Save"}
          </Button>
        </div>
      </div>

      {hasUnsaved && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2 text-xs" data-testid="unsaved-banner">
          <strong>{dirtyUserIds.length}</strong> user(s) have unsaved permission changes. Click <strong>Save</strong> to apply.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto max-h-[70vh] overflow-y-auto scroll-thin">
          <table className="min-w-max text-xs" data-testid="perm-matrix-table">
            <thead className="sticky top-0 z-10 bg-white">
              {/* Group header row */}
              <tr className="text-[10px] uppercase tracking-[0.14em] text-slate-500 border-b border-slate-100">
                <th className="text-left px-3 py-2 min-w-[240px] bg-white sticky left-0 z-20 border-r border-slate-100">
                  <div className="flex items-center gap-1"><UsersIcon className="w-3.5 h-3.5" /> User</div>
                </th>
                {Object.entries(grouped).map(([g, ms]) => (
                  <th key={g} colSpan={ms.length} className="px-2 py-2 text-center font-bold text-slate-600 bg-slate-50 border-l border-slate-200">{g}</th>
                ))}
                <th className="px-3 py-2 bg-white"></th>
              </tr>
              {/* Module header row */}
              <tr className="border-b-2 border-slate-200 bg-white">
                <th className="text-left px-3 py-2 min-w-[240px] sticky left-0 z-20 bg-white border-r border-slate-100"></th>
                {modules.map((m) => (
                  <th key={m.key} className="px-2 py-2 border-l border-slate-100 align-bottom" title={m.label}>
                    <div className="flex flex-col items-center gap-1">
                      <span className="[writing-mode:vertical-rl] rotate-180 text-[11px] font-semibold text-slate-700 whitespace-nowrap h-40 overflow-hidden text-ellipsis">{m.label}</span>
                      <button
                        onClick={() => setAllForModule(m.key, !filteredUsers.every((u) => isChecked(u, m.key)))}
                        className="text-slate-400 hover:text-blue-600"
                        title="Toggle column"
                        data-testid={`col-toggle-${m.key}`}
                      >
                        {filteredUsers.every((u) => isChecked(u, m.key)) ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </th>
                ))}
                <th className="px-3 py-2 text-center text-[10px] uppercase text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={modules.length + 2} className="py-10 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && filteredUsers.length === 0 && (
                <tr><td colSpan={modules.length + 2} className="py-10 text-center text-slate-500">No users match your search.</td></tr>
              )}
              {filteredUsers.map((u) => {
                const isDirty = dirty[u.id] !== undefined;
                const isEmpty = (dirty[u.id] !== undefined ? dirty[u.id].size : (u.module_permissions || []).length) === 0;
                return (
                  <tr key={u.id} className="border-t border-slate-100 hover:bg-blue-50/30" data-testid={`perm-row-${u.email}`}>
                    <td className="px-3 py-2 sticky left-0 bg-white hover:bg-blue-50/30 border-r border-slate-100 z-10">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-[10px] font-bold text-slate-600">
                          {(u.name || u.email).slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                            {u.name || u.email.split("@")[0]}
                            {isDirty && <span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" title="Unsaved" />}
                          </div>
                          <div className="text-[10px] text-slate-500 mono">{u.email} · <span className="uppercase">{u.role}</span>
                            {isEmpty && <span className="ml-1 text-emerald-700">(role default)</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    {modules.map((m) => (
                      <td key={m.key} className="px-2 py-1 text-center border-l border-slate-50">
                        <Checkbox
                          checked={isChecked(u, m.key)}
                          onCheckedChange={() => toggle(u, m.key)}
                          data-testid={`perm-cb-${u.email}-${m.key}`}
                          aria-label={`${u.email} · ${m.label}`}
                        />
                      </td>
                    ))}
                    <td className="px-2 py-1 text-center whitespace-nowrap">
                      <button onClick={() => setAllForUser(u, true)} className="text-[10px] text-blue-600 hover:underline mr-2" data-testid={`row-all-${u.email}`}>All</button>
                      <button onClick={() => setAllForUser(u, false)} className="text-[10px] text-slate-500 hover:underline" data-testid={`row-none-${u.email}`}>None</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-xs text-slate-500 flex items-start gap-2 border-t border-slate-100 pt-3">
        <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
        <div>
          <strong>How this works:</strong> If a user has zero ticked modules, their sidebar falls back to their role&apos;s default view (admin sees everything; other roles see their role menu).
          If they have <em>any</em> ticked module, only those modules appear in their sidebar.
          Sub-menu entries (Design pages, Production Flow stages, Incoming Inspection pages, CRM lists) have their own columns:
          ticking one also grants its module, and once any sub-entry of a module is ticked only the ticked sub-entries are shown
          (tick just the module to show all of them).
          Admins always have full access regardless of ticks.
        </div>
      </div>
    </div>
  );
}
